import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCategories, getProduct, adminCreateProduct, adminUpdateProduct } from '../../api';
import './AdminLayout.css';
import './AdminProductForm.css';

const EMPTY = {
  name: '', slug: '', description: '', price: '',
  originalPrice: '', stock: '100', category: '',
  tags: '', isBestseller: false, isFeatured: false, isActive: true,
  isCustomizable: false, customMinLength: '1', customMaxLength: '20',
};

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);       // File objects for upload
  const [previews, setPreviews] = useState([]);  // preview URLs + alt texts
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const fileRef = useRef();

  useEffect(() => {
    getCategories().then(r => setCategories(r.data));
    if (!isNew) {
      getProduct(id).then(r => {
        const p = r.data;
        setForm({
          name: p.name || '',
          slug: p.slug || '',
          description: p.description || '',
          price: p.price || '',
          originalPrice: p.originalPrice || '',
          stock: p.stock ?? 100,
          category: p.category?._id || p.category || '',
          tags: (p.tags || []).join(', '),
          isBestseller: p.isBestseller || false,
          isFeatured: p.isFeatured || false,
          isActive: p.isActive !== false,
          isCustomizable: p.isCustomizable || false,
          customMinLength: p.customMinLength ?? 1,
          customMaxLength: p.customMaxLength ?? 20,
        });
        setLoading(false);
      });
    }
  }, [id, isNew]);

  function set(key, val) {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: '' }));
    // Auto-generate slug from name
    if (key === 'name' && isNew) {
      const slug = val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setForm(f => ({ ...f, name: val, slug }));
    }
  }

  function handleFiles(selectedFiles) {
    const newFiles = Array.from(selectedFiles);
    setFiles(prev => [...prev, ...newFiles]);
    const newPreviews = newFiles.map(f => ({
      url: URL.createObjectURL(f),
      alt: '',
      isNew: true,
    }));
    setPreviews(prev => [...prev, ...newPreviews]);
  }

  function removePreview(index) {
    URL.revokeObjectURL(previews[index].url);
    setFiles(prev => { const n = [...prev]; n.splice(index, 1); return n; });
    setPreviews(prev => { const n = [...prev]; n.splice(index, 1); return n; });
  }

  function updateAlt(index, alt) {
    setPreviews(prev => prev.map((p, i) => i === index ? { ...p, alt } : p));
  }

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.price || Number(form.price) <= 0) e.price = 'Valid price is required';
    if (!form.category) e.category = 'Category is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (form.isCustomizable) {
      const min = Number(form.customMinLength);
      const max = Number(form.customMaxLength);
      if (!Number.isInteger(min) || min < 1 || min > 100) e.customMinLength = 'Enter 1–100';
      if (!Number.isInteger(max) || max < 1 || max > 100) e.customMaxLength = 'Enter 1–100';
      else if (max < min) e.customMaxLength = 'Must be ≥ min characters';
    }
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('slug', form.slug.trim());
      fd.append('description', form.description.trim());
      fd.append('price', form.price);
      if (form.originalPrice) fd.append('originalPrice', form.originalPrice);
      fd.append('stock', form.stock);
      fd.append('category', form.category);
      fd.append('tags', JSON.stringify(form.tags.split(',').map(t => t.trim()).filter(Boolean)));
      fd.append('isBestseller', form.isBestseller);
      fd.append('isFeatured', form.isFeatured);
      fd.append('isActive', form.isActive);
      fd.append('isCustomizable', form.isCustomizable);
      fd.append('customMinLength', form.customMinLength);
      fd.append('customMaxLength', form.customMaxLength);

      // Attach image files and their alt texts
      files.forEach((file, i) => {
        fd.append('images', file);
        fd.append(`imageAlt_${i}`, previews[i]?.alt || form.name);
      });

      if (isNew) {
        const { data } = await adminCreateProduct(fd);
        navigate(`/admin/products/${data._id}/images`, {
          state: { flash: 'Product created! Now manage its images.' }
        });
      } else {
        await adminUpdateProduct(id, {
          name: form.name, slug: form.slug, description: form.description,
          price: form.price, originalPrice: form.originalPrice || undefined,
          stock: form.stock, category: form.category,
          tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
          isBestseller: form.isBestseller, isFeatured: form.isFeatured, isActive: form.isActive,
          isCustomizable: form.isCustomizable,
          customMinLength: form.customMinLength, customMaxLength: form.customMaxLength,
        });
        navigate('/admin/products', { state: { flash: 'Product updated.' } });
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="admin-loading">Loading…</div>;

  return (
    <div style={{ padding: '0 0 40px' }}>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{isNew ? 'Add Product' : 'Edit Product'}</h1>
          <p className="admin-page-sub">{isNew ? 'Fill in the details below' : `Editing: ${form.name}`}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {!isNew && (
            <button className="btn-secondary" onClick={() => navigate(`/admin/products/${id}/images`)}>
              🖼️ Manage Images
            </button>
          )}
          <button className="btn-secondary" onClick={() => navigate('/admin/products')}>Cancel</button>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Basic info */}
        <div className="admin-card" style={{ padding: 24 }}>
          <h3 className="section-title">Basic Information</h3>
          <div className="form-row" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label>Product Name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Custom Gym Plate Keychain" />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label>Slug (URL)</label>
              <input value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="auto-generated" />
              <span className="form-hint">Auto-generated from name. Change only if needed.</span>
            </div>
            <div className="form-group full">
              <label>Description *</label>
              <textarea rows={4} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe your product…" />
              {errors.description && <span className="field-error">{errors.description}</span>}
            </div>
          </div>
        </div>

        {/* Pricing & Inventory */}
        <div className="admin-card" style={{ padding: 24 }}>
          <h3 className="section-title">Pricing & Inventory</h3>
          <div className="form-row" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label>Sale Price (₹) *</label>
              <input type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)} placeholder="399" />
              {errors.price && <span className="field-error">{errors.price}</span>}
            </div>
            <div className="form-group">
              <label>Original Price (₹) <span style={{ color: '#9ca3af', fontWeight: 400 }}>for strikethrough</span></label>
              <input type="number" min="0" value={form.originalPrice} onChange={e => set('originalPrice', e.target.value)} placeholder="599" />
            </div>
            <div className="form-group">
              <label>Stock</label>
              <input type="number" min="0" value={form.stock} onChange={e => set('stock', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="">Select category…</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              {errors.category && <span className="field-error">{errors.category}</span>}
            </div>
            <div className="form-group full">
              <label>Tags <span style={{ color: '#9ca3af', fontWeight: 400 }}>(comma separated)</span></label>
              <input value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="gym, keychain, personalised" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 24, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'isBestseller', label: '⭐ Mark as Bestseller' },
              { key: 'isFeatured', label: '✨ Mark as Featured' },
              { key: 'isActive', label: '✅ Active (visible on store)' },
            ].map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} style={{ accentColor: '#f59e0b', width: 16, height: 16 }} />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Customisation */}
        <div className="admin-card" style={{ padding: 24 }}>
          <h3 className="section-title">Customisation</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, marginTop: 16 }}>
            <input type="checkbox" checked={form.isCustomizable} onChange={e => set('isCustomizable', e.target.checked)} style={{ accentColor: '#f59e0b', width: 16, height: 16 }} />
            ✏️ Customised product (customer enters a name before adding to cart)
          </label>
          {form.isCustomizable && (
            <div className="form-row" style={{ marginTop: 16 }}>
              <div className="form-group">
                <label>Min characters *</label>
                <input type="number" min="1" max="100" value={form.customMinLength} onChange={e => set('customMinLength', e.target.value)} />
                {errors.customMinLength && <span className="field-error">{errors.customMinLength}</span>}
              </div>
              <div className="form-group">
                <label>Max characters *</label>
                <input type="number" min="1" max="100" value={form.customMaxLength} onChange={e => set('customMaxLength', e.target.value)} />
                {errors.customMaxLength && <span className="field-error">{errors.customMaxLength}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Images (only shown for new products — existing products use the dedicated image manager) */}
        {isNew && (
          <div className="admin-card" style={{ padding: 24 }}>
            <h3 className="section-title">Images <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: 13 }}>— you can add more after saving</span></h3>

            {/* Drop zone */}
            <div
              className="dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            >
              <span style={{ fontSize: 28 }}>📁</span>
              <p>Click or drag images here</p>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>JPG, PNG, WebP · max 5MB each</span>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => handleFiles(e.target.files)} />

            {/* Previews with alt text inputs */}
            {previews.length > 0 && (
              <div className="image-preview-grid" style={{ marginTop: 16 }}>
                {previews.map((p, i) => (
                  <div key={i} className="preview-item">
                    <div className="preview-img-wrap">
                      <img src={p.url} alt="preview" />
                      {i === 0 && <span className="primary-badge">Primary</span>}
                      <button type="button" className="preview-remove" onClick={() => removePreview(i)}>✕</button>
                    </div>
                    <input
                      type="text"
                      placeholder="Image description (alt text)"
                      value={p.alt}
                      onChange={e => updateAlt(i, e.target.value)}
                      className="alt-input"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/products')}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isNew ? '✓ Create Product' : '✓ Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
